import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Platform = 'ios' | 'android' | 'desktop';

/** Detect the user's platform from `navigator.userAgent`. iPadOS 13+
 *  pretends to be macOS, so we also check for touch support to keep
 *  iPads on the iOS skin. Returns once on mount and never recomputes
 *  — no need to react to user-agent changes within a session. */
function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  const isIpadOs = /MacIntel/.test(navigator.platform) && (navigator.maxTouchPoints || 0) > 1;
  if (/iPad|iPhone|iPod/.test(ua) || isIpadOs) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

export interface PlatformConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
}

/** Confirmation modal that mimics the platform's native alert sheet:
 *
 *   iOS / iPadOS  → centered ~270px UIAlertController-style card with
 *                   a horizontal divided action row (red destructive
 *                   text on a translucent panel).
 *   Android       → Material 3 dialog: 28px-rounded surface, sentence-
 *                   case text actions aligned to the right.
 *   Desktop       → app-styled Dialog using our shared Button.
 *
 *  The iOS / Android variants bypass our regular `DialogContent`
 *  (which goes full-screen on `<lg`) and render Radix primitives
 *  directly so the dialog stays a small centered card on phones —
 *  full-screen confirmations feel wrong on mobile.
 */
export function PlatformConfirmDialog({
  open, onOpenChange, title, description,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  destructive = false, loading = false, onConfirm,
}: PlatformConfirmProps) {
  const [platform] = React.useState<Platform>(detectPlatform);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50',
            platform === 'ios'
              ? 'bg-black/40 backdrop-blur-sm'
              : platform === 'android'
                ? 'bg-black/60'
                : 'bg-black/80',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />

        {platform === 'ios' && (
          <DialogPrimitive.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
              'w-[270px] rounded-[14px] overflow-hidden',
              'bg-white/95 dark:bg-zinc-800/95 backdrop-blur-2xl shadow-xl',
              'data-[state=open]:animate-in data-[state=open]:zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:zoom-out-95',
            )}
          >
            <div className="px-4 pt-4 pb-3 text-center">
              <DialogPrimitive.Title className="text-[17px] font-semibold leading-tight text-foreground">
                {title}
              </DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="text-[13px] text-foreground/80 mt-1.5 leading-snug">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
            <div className="border-t border-black/10 dark:border-white/10 grid grid-cols-2 divide-x divide-black/10 dark:divide-white/10">
              <button
                type="button"
                className="py-3 text-[17px] text-blue-600 dark:text-blue-400 font-medium hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                className={cn(
                  'py-3 text-[17px] font-semibold hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50',
                  destructive ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400',
                )}
                onClick={onConfirm}
                disabled={loading}
              >
                {loading ? '…' : confirmLabel}
              </button>
            </div>
          </DialogPrimitive.Content>
        )}

        {platform === 'android' && (
          <DialogPrimitive.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
              'w-[min(360px,calc(100vw-32px))] rounded-[28px]',
              'bg-background border border-border p-6 shadow-2xl',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            )}
          >
            <DialogPrimitive.Title className="text-[24px] font-normal leading-tight">
              {title}
            </DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="text-[14px] text-foreground/80 leading-relaxed mt-4">
                {description}
              </DialogPrimitive.Description>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                className="px-3 py-2 rounded-full text-[14px] font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                className={cn(
                  'px-3 py-2 rounded-full text-[14px] font-medium disabled:opacity-50',
                  destructive
                    ? 'text-red-600 dark:text-red-400 hover:bg-red-500/10'
                    : 'text-primary hover:bg-primary/10',
                )}
                onClick={onConfirm}
                disabled={loading}
              >
                {loading ? 'Working…' : confirmLabel}
              </button>
            </div>
          </DialogPrimitive.Content>
        )}

        {platform === 'desktop' && (
          <DialogPrimitive.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
              'w-[min(420px,calc(100vw-32px))] rounded-lg',
              'bg-background border p-6 shadow-lg',
              'data-[state=open]:animate-in data-[state=open]:zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:zoom-out-95',
            )}
          >
            <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
              {title}
            </DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="text-sm text-muted-foreground mt-2">
                {description}
              </DialogPrimitive.Description>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                {cancelLabel}
              </Button>
              <Button
                variant={destructive ? 'destructive' : 'default'}
                onClick={onConfirm}
                disabled={loading}
              >
                {loading ? 'Working…' : confirmLabel}
              </Button>
            </div>
          </DialogPrimitive.Content>
        )}
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
